import { useTranslation } from "react-i18next";
import { Q3DDataTable, Q3DDataTableHeaderType } from "../../Parts/Q3DDataTable";
import { useContext, useEffect, useState } from "react";
import HelmContext from "../Contexts/HelmContext";
import {
  DETAIL_TAB2POINT_CLOUD_TYPE,
  EDialogType,
  EDisplayType,
  EViewerMode,
  ProjectDetailContext,
} from "../../../pages/ProjectDetail";
import { ECoordinateTransformMode, EMPTY } from "../../config/componentsConfig";
import PointUtil from "../../Utility/PointUtil";
import _ from "lodash";
import { Common } from "../../../../common/src";
import { Q3DHintPopover } from "../../Parts/Q3DHintPopOver";
import { POINT_CLOUD_TYPE } from "../../../../common/src/modules";

type Props = {};

export const HelmTransformContent = (props: Props) => {
  const { t } = useTranslation();
  const {
    projectDetail,
    updateCurrentDialog,
    detailTab,
    viewer,
    currentViewerMode,
    currentPointCloud,
    displayType,
  } = useContext(ProjectDetailContext);
  const {
    currentHelmTab,
    coordinatePoints,
    referencePoints,
    setReferencePoints,
    fetchReferencePoints,
    handleDelete,
    handleAdd,
    handleSave,
    handlePicking,
    handleAllPickPoints,
  } = useContext(HelmContext);

  // 必要データのfetch
  useEffect(() => {
    const abortController = new AbortController();
    const abortControllerSignal = abortController.signal;
    fetchReferencePoints(projectDetail?.id, abortControllerSignal);

    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    if (
      currentHelmTab === ECoordinateTransformMode.TRANSFORM &&
      currentPointCloud === POINT_CLOUD_TYPE.SFM
    ) {
      handleAllPickPoints(ECoordinateTransformMode.TRANSFORM);
    }

    return () => {
      viewer.deleteAllPickPoint();
    };
  }, [currentHelmTab, currentPointCloud]);

  useEffect(() => {
    const operation =
      isReferencePickable() &&
      currentViewerMode === EViewerMode.HELMERT_TRANSFORM
        ? "start"
        : "end";
    handlePicking(operation, ECoordinateTransformMode.TRANSFORM);
  }, [detailTab, currentHelmTab, referencePoints]);

  const isReferencePickable = () => {
    return (
      referencePoints[referencePoints.length - 1]?.name === EMPTY &&
      DETAIL_TAB2POINT_CLOUD_TYPE(detailTab) === Common.POINT_CLOUD_TYPE.SFM &&
      currentHelmTab === ECoordinateTransformMode.TRANSFORM
    );
  };

  const headers = [
    {
      key: "tool",
      name: "",
      type: "icon",
    },
    {
      key: "correspondingPointId",
      name: t("POINT_NAME"),
      type: "select",
    },
    {
      key: "timeStamp",
      name: t("DATE"),
      type: "date",
    },
    {
      key: "correspondingX",
      name: t("X_COORDINATE_AFTER_CONVERSION"),
      type: "number",
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "19px",
      },
    },
    {
      key: "correspondingY",
      name: t("Y_COORDINATE_AFTER_CONVERSION"),
      type: "number",
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "19px",
      },
    },
    {
      key: "correspondingZ",
      name: t("Z_COORDINATE_AFTER_CONVERSION"),
      type: "number",
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "19px",
      },
    },
    {
      key: "y",
      name: t("ORIGINAL_X"),
      type: "number",
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "19px",
      },
    },
    {
      key: "x",
      name: t("ORIGINAL_Y"),
      type: "number",
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "19px",
      },
    },
    {
      key: "z",
      name: t("ORIGINAL_Z"),
      type: "number",
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "19px",
      },
    },
    {
      key: "dx",
      name: t("X_ERROR"),
      type: "number",
      rightIcon: (
        <Q3DHintPopover
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          content={
            t("X_ERROR") +
            " = " +
            t("X_COORDINATE_AFTER_CONVERSION") +
            " - " +
            t("ORIGINAL_X")
          }
        />
      ),
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "15px",
      },
    },
    {
      key: "dy",
      name: t("Y_ERROR"),
      type: "number",
      rightIcon: (
        <Q3DHintPopover
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          content={
            t("Y_ERROR") +
            " = " +
            t("Y_COORDINATE_AFTER_CONVERSION") +
            " - " +
            t("ORIGINAL_Y")
          }
        />
      ),
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "15px",
      },
    },
    {
      key: "dz",
      name: t("Z_ERROR"),
      type: "number",
      rightIcon: (
        <Q3DHintPopover
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          content={
            t("Z_ERROR") +
            " = " +
            t("Z_COORDINATE_AFTER_CONVERSION") +
            " - " +
            t("ORIGINAL_Z")
          }
        />
      ),
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "15px",
      },
    },
  ] as Q3DDataTableHeaderType[];

  const updateRowsData = (id: string, key: string, value: string) => {
    setReferencePoints(
      referencePoints.map((data) => {
        if (data.id === id) {
          data.correspondingPointId = value;
          (data as any)[key] = value;
          if (key === "correspondingPointId") {
            const correspondingPoint = coordinatePoints?.find(
              (correspondPoint) => correspondPoint.id === value
            );
            data.correspondingX = correspondingPoint?.x
              ? correspondingPoint.x
              : 0;
            data.correspondingY = correspondingPoint?.y
              ? correspondingPoint.y
              : 0;
            data.correspondingZ = correspondingPoint?.z
              ? correspondingPoint.z
              : 0;
            data.timeStamp = correspondingPoint?.timeStamp
              ? correspondingPoint.timeStamp
              : new Date(0);
          }
        }
        return data;
      })
    );
    handleAllPickPoints(ECoordinateTransformMode.TRANSFORM);
  };

  const saveFunction = (id: string, key: string, value: string) => {
    try {
      if (value !== "" && id !== "" && key !== "") {
        PointUtil.updateReference(
          id,
          key,
          value,
          projectDetail.id,
          referencePoints
        );
      }
    } catch (err) {
      console.error("Error Msg:", err);
    }
  };
  // 保存制御処理
  const debouncedSave = _.debounce(saveFunction, 1000);

  return (
    <Q3DDataTable
      headers={headers}
      rows={referencePoints}
      selectItems={coordinatePoints.map((item) => {
        return { label: item.pointName, value: item.id };
      })}
      onClickHeaderButton={() => {
        updateCurrentDialog(EDialogType.DELETE_ALL_POINTS);
      }}
      onClickRowButton={(row: string, isSave: boolean) => {
        if (isSave) {
          handleSave(ECoordinateTransformMode.TRANSFORM, row);
        } else {
          handleDelete(ECoordinateTransformMode.TRANSFORM, row);
        }
      }}
      onClickBottomButton={() => {
        handleAdd(ECoordinateTransformMode.TRANSFORM);
      }}
      onChange={(id: string, key: string, value: any) => {
        updateRowsData(id, key, value);
        if (id !== "new") {
          debouncedSave(id, key, value);
        }
      }}
    />
  );
};
